import { useRef, useState } from 'react';
import { Box, IconButton, CircularProgress, Snackbar, AspectRatio } from '@mui/joy';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EventIcon from '@mui/icons-material/Event';
import { useUpdateEventMutation, useUploadEventImageMutation } from '@/features/events/api';
import { statusCodeToMessage, extractUploadErrorMessage } from '@/utils/errors';
import { IMAGE_UPLOAD_ACCEPT } from '@/utils/images';
import { InlineEditControls } from '@/components/InlineEditControls';
import { EventRowShell } from './EventRowShell';

// The editing state of an events settings row: name + photo, both changeable
// in place. Split out from EventRow because it owns a fair bit on its own --
// the pending name/photo, the upload, and the save -- versus the read-only
// row's plain display + edit/delete buttons.
export function EditingEventRow({ event, householdId, onStopEdit }) {
  const [updateEvent, { isLoading: isUpdating }] = useUpdateEventMutation();
  const [uploadEventImage, { isLoading: isUploadingImage }] = useUploadEventImageMutation();
  const [editValue, setEditValue] = useState(event.name);
  const [editImage, setEditImage] = useState(event.image);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleSave = async () => {
    const name = editValue.trim();
    if (!name) return;

    setError(null);
    const data = { name };
    if (editImage?.file_name !== event.image?.file_name || editImage?.ext !== event.image?.ext) {
      data.image = editImage;
    }
    try {
      await updateEvent({ householdId, id: event.id, data }).unwrap();
      onStopEdit();
    } catch (err) {
      setError(statusCodeToMessage(err.status));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    try {
      const result = await uploadEventImage({ householdId, file }).unwrap();
      setEditImage({ ...result.data, url: URL.createObjectURL(file) });
    } catch (err) {
      setError(extractUploadErrorMessage(err));
    }
  };

  return (
    <EventRowShell>
      <Box sx={{ position: 'relative', width: 36, flexShrink: 0, ml: '-2px' }}>
        <AspectRatio ratio="1" sx={{ width: 36, borderRadius: 'sm'  }}>
          {editImage?.url ? (
            <img src={editImage.url} alt={event.name} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.tertiary' }}>
              <EventIcon fontSize="small" />
            </Box>
          )}
        </AspectRatio>
        <IconButton
          size="sm"
          variant="solid"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploadingImage}
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 'sm',
            bgcolor: 'rgba(0, 0, 0, 0.45)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.6)' },
          }}
        >
          {isUploadingImage ? (
            <CircularProgress size="sm" sx={{ '--CircularProgress-trackColor': 'transparent', '--CircularProgress-progressColor': 'white' }} />
          ) : (
            <PhotoCameraIcon fontSize="small" sx={{ color: 'white' }} />
          )}
        </IconButton>
      </Box>
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_UPLOAD_ACCEPT}
        style={{ display: 'none' }}
        onChange={handleImageUpload}
      />
      <InlineEditControls
        value={editValue}
        onChange={setEditValue}
        onSave={handleSave}
        onCancel={onStopEdit}
        isPersisting={isUpdating}
      />
      <Snackbar
        color="danger"
        variant="soft"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={!!error}
        onClose={() => setError(null)}
      >
        {error}
      </Snackbar>
    </EventRowShell>
  );
}
